import {
  CSSProperties,
  ElementType,
  ComponentPropsWithoutRef,
  useMemo,
} from "react";
import clsx from "clsx";
import { createSpacingClasses, SpacingProps } from "@theme/spacing.ts";
import { ColorProps, createColorClasses } from "@theme/colors.ts";
import { createTypographyClasses, TypographyProps } from "@theme/typography.ts";

type PolymorphicProps<E extends ElementType> = {
  as?: E;
  justifyContent?: CSSProperties["justifyContent"];
  alignItems?: CSSProperties["alignItems"];
  flexWrap?: CSSProperties["flexWrap"];
  ref?: React.Ref<HTMLDivElement>;
} & SpacingProps &
  ColorProps &
  TypographyProps &
  Omit<
    ComponentPropsWithoutRef<E>,
    keyof SpacingProps | keyof ColorProps | keyof TypographyProps
  >;

export const Col = <E extends ElementType = "div">({
  as,
  className,
  justifyContent,
  alignItems,
  flexWrap,
  ref,
  style,
  ...rest
}: PolymorphicProps<E>) => {
  const Component = as || "div";

  const classNames = useMemo(() => {
    const spacingClasses = createSpacingClasses(rest);
    const colorClasses = createColorClasses(rest);
    const typographyClasses = createTypographyClasses(rest);
    return [...spacingClasses, ...colorClasses, ...typographyClasses].join(" ");
  }, [rest]);

  return (
    <Component
      ref={ref}
      className={clsx("base-col", className, classNames)}
      style={{
        justifyContent,
        alignItems,
        flexWrap,
        ...style,
      }}
      {...rest}
    />
  );
};
